import React from 'react';
import { Menu, MenuItem } from '@mui/material';
import {
  Edit as EditIcon,
  Key as PasswordIcon,
  Check as VerifiedIcon,
  Close as RejectedIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { Device } from '../../types/api';

interface DeviceActionsMenuProps {
  anchorEl: HTMLElement | null;
  device: Device | null;
  isVerifying: boolean;
  isRejecting: boolean;
  isChangingPasswords: boolean;
  onClose: () => void;
  onViewDetails: (device: Device) => void;
  onVerify: (device: Device) => void;
  onReject: (device: Device) => void;
  onChangePasswords: (device: Device) => void;
  onArchive: (device: Device) => void;
}

const VERIFIABLE_STATUSES = ['pending', 'enrolled', 'unverified_suspicious'];
const PASSWORD_CHANGE_STATUSES = ['enrolled', 'verified'];

/** Row-level actions menu for a single device (view, verify, reject, etc.). */
const DeviceActionsMenu: React.FC<DeviceActionsMenuProps> = ({
  anchorEl,
  device,
  isVerifying,
  isRejecting,
  isChangingPasswords,
  onClose,
  onViewDetails,
  onVerify,
  onReject,
  onChangePasswords,
  onArchive,
}) => {
  const regStatus = device?.registrationStatus;
  const canVerify = !!regStatus && VERIFIABLE_STATUSES.includes(regStatus);
  const canChangePasswords = !!regStatus && PASSWORD_CHANGE_STATUSES.includes(regStatus);

  // Wrap a device action so the menu always closes afterwards.
  const run = (action: (device: Device) => void) => () => {
    if (device) action(device);
    onClose();
  };

  return (
    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
      <MenuItem onClick={run(onViewDetails)}>
        <EditIcon sx={{ mr: 1 }} fontSize="small" />
        View Details
      </MenuItem>
      <MenuItem onClick={onClose}>
        <EditIcon sx={{ mr: 1 }} fontSize="small" />
        Edit
      </MenuItem>
      {canVerify && [
        <MenuItem key="verify" onClick={run(onVerify)} disabled={isVerifying}>
          <VerifiedIcon sx={{ mr: 1 }} fontSize="small" />
          Verify Device
        </MenuItem>,
        <MenuItem key="reject" onClick={run(onReject)} disabled={isRejecting}>
          <RejectedIcon sx={{ mr: 1 }} fontSize="small" />
          Reject Device
        </MenuItem>,
      ]}
      {canChangePasswords && (
        <MenuItem onClick={run(onChangePasswords)} disabled={isChangingPasswords}>
          <PasswordIcon sx={{ mr: 1 }} fontSize="small" />
          Change Passwords
        </MenuItem>
      )}
      <MenuItem onClick={run(onArchive)}>
        <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
        Archive
      </MenuItem>
    </Menu>
  );
};

export default DeviceActionsMenu;
